import { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { usersAPI, storesAPI, timeclockAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { WebhookManager } from '../components/WebhookManager';
import { ApiKeyManager } from '../components/ApiKeyManager';
import { StripeConnectSettings } from '../components/StripeConnectSettings';
import { toast } from 'sonner';
import {
    Users, Sun, Moon, Monitor, MoreVertical, Trash2, Shield,
    CreditCard, Percent, DollarSign, Clock, Loader2, Building, UserPlus,
    UtensilsCrossed, Coffee, ShoppingBag, Wine, ShoppingCart, Scissors, Fuel, HelpCircle,
    CheckCircle2, User, Settings as SettingsIcon, Palette, KeyRound, Eye, EyeOff,
    BadgeCheck, AlertCircle, Crown, Zap, Lock, ChevronRight, Activity, Key, Webhook
} from 'lucide-react';

const PLAN_LIMITS = { starter: 2, professional: 10, enterprise: 9999 };

const PLAN_FEATURES = {
    starter: [
        'Up to 2 staff accounts',
        'Unlimited products',
        'Sales history & reports',
        'Basic inventory tracking',
        'Cash & card payments',
    ],
    professional: [
        'Up to 10 staff accounts',
        'Everything in Starter',
        'Advanced analytics',
        'Multi-category management',
        'Auto-reorder with suppliers',
        'Time clock & shift tracking',
    ],
    enterprise: [
        'Unlimited staff accounts',
        'Everything in Professional',
        'Dedicated support',
        'Custom integrations',
        'API access',
        'White-label options',
    ],
};

const PLAN_COLORS = {
    starter: { gradient: 'from-slate-700 to-slate-900', badge: 'bg-slate-600', accent: 'bg-cyan-500' },
    professional: { gradient: 'from-cyan-700 to-cyan-900', badge: 'bg-cyan-600', accent: 'bg-cyan-400' },
    enterprise: { gradient: 'from-violet-700 to-violet-900', badge: 'bg-violet-600', accent: 'bg-violet-400' },
};

const JOB_TITLE_OPTIONS = [
    'Cashier', 'Server', 'Bartender', 'Host/Hostess', 'Cook', 'Barista',
    'Supervisor', 'Shift Lead', 'Stock Clerk', 'Delivery Driver', 'Other'
];

const BUSINESS_TYPE_OPTIONS = [
    { key: "restaurant", label: "Restaurant", desc: "Restaurants, food trucks, diners", icon: UtensilsCrossed, color: "text-orange-500", bg: "bg-orange-500/10 border-orange-500/40" },
    { key: "cafe", label: "Café / Coffee", desc: "Coffee shops, juice bars", icon: Coffee, color: "text-amber-500", bg: "bg-amber-500/10 border-amber-500/40" },
    { key: "retail", label: "Retail Store", desc: "Clothing, electronics, gifts", icon: ShoppingBag, color: "text-blue-500", bg: "bg-blue-500/10 border-blue-500/40" },
    { key: "liquor", label: "Liquor Store", desc: "Liquor, wine, beer", icon: Wine, color: "text-purple-500", bg: "bg-purple-500/10 border-purple-500/40" },
    { key: "grocery", label: "Grocery", desc: "Grocery, supermarket", icon: ShoppingCart, color: "text-emerald-500", bg: "bg-emerald-500/10 border-emerald-500/40" },
    { key: "salon", label: "Salon / Spa", desc: "Hair, nails, spa", icon: Scissors, color: "text-pink-500", bg: "bg-pink-500/10 border-pink-500/40" },
    { key: "convenience", label: "Convenience", desc: "Gas stations, mini-marts", icon: Fuel, color: "text-yellow-500", bg: "bg-yellow-500/10 border-yellow-500/40" },
    { key: "other", label: "Other", desc: "Any other business", icon: HelpCircle, color: "text-slate-500", bg: "bg-slate-500/10 border-slate-500/40" }
];

const ROLE_COLORS = {
    admin: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20',
    manager: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20',
    cashier: 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20',
};

export default function Settings() {
    const { user } = useAuth();
    const { theme, setTheme } = useTheme();
    const [users, setUsers] = useState([]);
    const [pinStatuses, setPinStatuses] = useState({});
    const [loading, setLoading] = useState(true);
    const [savingFees, setSavingFees] = useState(false);
    const [savingInfo, setSavingInfo] = useState(false);
    const [savingTimeclock, setSavingTimeclock] = useState(false);
    const [addingUser, setAddingUser] = useState(false);
    const [showAddUser, setShowAddUser] = useState(false);
    const [timeclockSettings, setTimeclockSettings] = useState({ require_clockin_for_pos: false });
    const [storeSettings, setStoreSettings] = useState({
        card_fee_percentage: 0, card_fee_flat: 0,
        show_fee_to_customer: true, tax_rate: 8,
        subscription_status: null, trial_end: null,
        subscription_plan: 'starter'
    });
    const [storeInfo, setStoreInfo] = useState({ name: '', address: '', phone: '', email: '' });
    const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'cashier', job_title: '', pin: '', confirmPin: '' });
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [businessType, setBusinessType] = useState('');
    const [applyingTemplate, setApplyingTemplate] = useState(false);

    const [setPinDialogOpen, setSetPinDialogOpen] = useState(false);
    const [setPinTarget, setSetPinTarget] = useState(null);
    const [newPinValue, setNewPinValue] = useState('');
    const [confirmPinValue, setConfirmPinValue] = useState('');
    const [savingPin, setSavingPin] = useState(false);

    const [showChangePassword, setShowChangePassword] = useState(false);
    const [showCurrentPw, setShowCurrentPw] = useState(false);
    const [changingPw, setChangingPw] = useState(false);
    const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirmPw: '' });

    const isAdmin = user?.role === 'admin';
    const isManager = user?.role === 'manager';
    const canManageUsers = isAdmin || isManager;
    const isSuperAdmin = isAdmin && !user?.store_id;

    useEffect(() => {
        if (canManageUsers) fetchUsers();
        if (isAdmin) {
            fetchStoreSettings();
            fetchTimeclockSettings();
            if (user?.store_id) fetchStoreInfo();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchUsers = async () => {
        try {
            const [usersRes, pinsRes] = await Promise.all([
                usersAPI.getAll(),
                user?.store_id ? timeclockAPI.listPins() : Promise.resolve({ data: [] })
            ]);
            setUsers(usersRes.data);
            const pinMap = {};
            (pinsRes.data || []).forEach(p => { pinMap[p.user_id] = p; });
            setPinStatuses(pinMap);
        } catch { console.log('Could not fetch users'); }
        finally { setLoading(false); }
    };

    const fetchStoreSettings = async () => {
        try {
            const response = await storesAPI.getSettings();
            setStoreSettings(response.data);
        } catch { console.log('Could not fetch store settings'); }
    };

    const fetchStoreInfo = async () => {
        try {
            const response = await storesAPI.getInfo();
            const s = response.data;
            setStoreInfo({ name: s.name || '', address: s.address || '', phone: s.phone || '', email: s.email || '' });
            setBusinessType(s.business_type || 'retail');
            setStoreSettings(prev => ({ ...prev, subscription_plan: s.subscription_plan || 'starter' }));
        } catch { console.log('Could not fetch store info'); }
    };

    const fetchTimeclockSettings = async () => {
        try {
            const response = await timeclockAPI.getSettings();
            setTimeclockSettings(response.data);
        } catch { console.log('Could not fetch timeclock settings'); }
    };

    const handleApplyTemplate = async (type) => {
        setApplyingTemplate(true);
        try {
            const res = await storesAPI.applyTemplate({ business_type: type });
            setBusinessType(type);
            toast.success(res.data.message);
        } catch { toast.error('Failed to apply template'); }
        finally { setApplyingTemplate(false); }
    };

    const handleSaveInfo = async () => {
        setSavingInfo(true);
        try {
            await storesAPI.updateInfo(storeInfo);
            toast.success('Business info saved!');
        } catch { toast.error('Failed to save business info'); }
        finally { setSavingInfo(false); }
    };

    const handleSaveTimeclock = async () => {
        setSavingTimeclock(true);
        try {
            await timeclockAPI.updateSettings({ require_clockin_for_pos: timeclockSettings.require_clockin_for_pos });
            toast.success('Time clock settings saved!');
        } catch { toast.error('Failed to save time clock settings'); }
        finally { setSavingTimeclock(false); }
    };

    const handleSaveFees = async () => {
        setSavingFees(true);
        try {
            await storesAPI.updateSettings({
                card_fee_percentage: parseFloat(storeSettings.card_fee_percentage) || 0,
                card_fee_flat: parseFloat(storeSettings.card_fee_flat) || 0,
                show_fee_to_customer: storeSettings.show_fee_to_customer,
                tax_rate: parseFloat(storeSettings.tax_rate) || 8
            });
            toast.success('Payment settings saved!');
        } catch { toast.error('Failed to save settings'); }
        finally { setSavingFees(false); }
    };

    const handleRoleChange = async (userId, newRole) => {
        try {
            await usersAPI.updateRole(userId, newRole);
            toast.success('Role updated');
            fetchUsers();
        } catch { toast.error('Failed to update role'); }
    };

    const handleDeleteUser = async (userId) => {
        if (userId === user?.id) { toast.error('Cannot delete your own account'); return; }
        if (!window.confirm('Delete this user? Their PIN will also be removed.')) return;
        try {
            await usersAPI.delete(userId);
            toast.success('User deleted');
            fetchUsers();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete user'); }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        if (newUser.pin && newUser.pin !== newUser.confirmPin) {
            toast.error('PINs do not match');
            return;
        }
        if (newUser.pin && (newUser.pin.length !== 4 || !/^\d{4}$/.test(newUser.pin))) {
            toast.error('PIN must be exactly 4 digits');
            return;
        }
        setAddingUser(true);
        try {
            await usersAPI.create({
                name: newUser.name,
                email: newUser.email,
                password: newUser.password,
                role: newUser.role,
                job_title: newUser.job_title || null,
                pin: newUser.pin || null,
                store_id: user?.store_id
            });
            toast.success(`${newUser.name} created${newUser.pin ? ' with PIN' : ''}`);
            setNewUser({ name: '', email: '', password: '', role: 'cashier', job_title: '', pin: '', confirmPin: '' });
            setShowAddUser(false);
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create user');
        } finally { setAddingUser(false); }
    };

    const handleOpenSetPin = (targetUser) => {
        setSetPinTarget(targetUser);
        setNewPinValue('');
        setConfirmPinValue('');
        setSetPinDialogOpen(true);
    };

    const handleSavePin = async () => {
        if (!newPinValue || newPinValue.length !== 4 || !/^\d{4}$/.test(newPinValue)) {
            toast.error('PIN must be exactly 4 digits (numbers only)');
            return;
        }
        if (newPinValue !== confirmPinValue) {
            toast.error('PINs do not match');
            return;
        }
        setSavingPin(true);
        try {
            await timeclockAPI.createPin({ user_id: setPinTarget.id, pin: newPinValue });
            toast.success(`PIN set for ${setPinTarget.name}`);
            setSetPinDialogOpen(false);
            fetchUsers();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to set PIN');
        } finally { setSavingPin(false); }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (pwForm.newPw !== pwForm.confirmPw) { toast.error('New passwords do not match'); return; }
        if (pwForm.newPw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
        setChangingPw(true);
        try {
            await usersAPI.changePassword({ current_password: pwForm.current, new_password: pwForm.newPw });
            toast.success('Password changed successfully!');
            setPwForm({ current: '', newPw: '', confirmPw: '' });
            setShowChangePassword(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to change password');
        } finally { setChangingPw(false); }
    };

    const getTrialDaysRemaining = () => {
        if (!storeSettings.trial_end) return null;
        const days = Math.ceil((new Date(storeSettings.trial_end) - new Date()) / (1000 * 60 * 60 * 24));
        return days > 0 ? days : 0;
    };

    const trialDays = getTrialDaysRemaining();
    const plan = storeSettings.subscription_plan || 'starter';
    const userLimit = PLAN_LIMITS[plan] || 2;
    const storeUsers = users.filter(u => u.store_id === user?.store_id);

    return (
        <Layout title="Settings">
            <div className="max-w-4xl space-y-6" data-testid="settings-page">

                {storeSettings.subscription_status === 'trial' && trialDays !== null && (
                    <div className="flex items-center justify-between p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/30">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                                <Clock className="h-5 w-5 text-cyan-500" />
                            </div>
                            <div>
                                <p className="font-bold text-cyan-600 dark:text-cyan-400">Free Trial Active</p>
                                <p className="text-sm text-muted-foreground">{trialDays} days remaining — upgrade to keep full access</p>
                            </div>
                        </div>
                        <Button size="sm" className="bg-cyan-500 hover:bg-cyan-600 text-white font-semibold">
                            <Zap className="h-3.5 w-3.5 mr-1.5" /> Upgrade Plan
                        </Button>
                    </div>
                )}

                <Tabs defaultValue={canManageUsers ? (isAdmin ? "general" : "users") : "system"} className="space-y-6">
                    <TabsList className={`grid w-full h-12 p-1 bg-muted/60 ${isAdmin ? 'grid-cols-5' : 'grid-cols-3'}`}>
                        {isAdmin && (
                            <TabsTrigger value="general" className="flex items-center gap-1.5 data-[state=active]:bg-cyan-500 data-[state=active]:text-white data-[state=active]:shadow-sm font-semibold text-sm rounded-md">
                                <Building className="h-4 w-4 shrink-0" /><span className="truncate">Business</span>
                            </TabsTrigger>
                        )}
                        {isAdmin && (
                            <TabsTrigger value="payments" className="flex items-center gap-1.5 data-[state=active]:bg-cyan-500 data-[state=active]:text-white data-[state=active]:shadow-sm font-semibold text-sm rounded-md">
                                <CreditCard className="h-4 w-4 shrink-0" /><span className="truncate">Payments</span>
                            </TabsTrigger>
                        )}
                        {canManageUsers && (
                            <TabsTrigger value="users" className="flex items-center gap-1.5 data-[state=active]:bg-cyan-500 data-[state=active]:text-white data-[state=active]:shadow-sm font-semibold text-sm rounded-md">
                                <Users className="h-4 w-4 shrink-0" /><span className="truncate">Team</span>
                            </TabsTrigger>
                        )}
                        <TabsTrigger value="webhooks" className="flex items-center gap-1.5 data-[state=active]:bg-cyan-500 data-[state=active]:text-white data-[state=active]:shadow-sm font-semibold text-sm rounded-md">
                            <Webhook className="h-4 w-4 shrink-0" /><span className="truncate">Webhooks</span>
                        </TabsTrigger>
                        <TabsTrigger value="system" className="flex items-center gap-1.5 data-[state=active]:bg-cyan-500 data-[state=active]:text-white data-[state=active]:shadow-sm font-semibold text-sm rounded-md">
                            <SettingsIcon className="h-4 w-4 shrink-0" /><span className="truncate">System</span>
                        </TabsTrigger>
                    </TabsList>

                    {/* ── BUSINESS TAB ── */}
                    {isAdmin && (
                        <TabsContent value="general" className="space-y-6">

                            {/* Plan Overview Card */}
                            {user?.store_id && (() => {
                                const planColor = PLAN_COLORS[plan] || PLAN_COLORS.starter;
                                const usedPct = userLimit === 9999 ? 8 : Math.min((storeUsers.length / userLimit) * 100, 100);
                                const nearLimit = storeUsers.length >= userLimit * 0.8 && userLimit !== 9999;
                                return (
                                    <Card className="shadow-sm overflow-hidden">
                                        <div className={`bg-gradient-to-r ${planColor.gradient} text-white`}>
                                            <div className="p-5 flex items-start justify-between gap-4">
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Crown className="h-4 w-4 text-amber-300 shrink-0" />
                                                        <span className="text-xs font-bold uppercase tracking-widest text-white/60">Active Plan</span>
                                                    </div>
                                                    <p className="text-3xl font-black capitalize">{plan}</p>
                                                    <div className="flex items-center gap-2 mt-1.5">
                                                        {storeSettings.subscription_status === 'trial' && trialDays !== null ? (
                                                            <span className="inline-flex items-center gap-1.5 text-xs bg-amber-400/20 text-amber-300 border border-amber-400/30 rounded-full px-2.5 py-0.5 font-semibold">
                                                                <Clock className="h-3 w-3" />{trialDays}d trial left
                                                            </span>
                                                        ) : storeSettings.subscription_status === 'active' ? (
                                                            <span className="inline-flex items-center gap-1.5 text-xs bg-emerald-400/20 text-emerald-300 border border-emerald-400/30 rounded-full px-2.5 py-0.5 font-semibold">
                                                                <Activity className="h-3 w-3" />Active
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1.5 text-xs bg-white/10 text-white/70 rounded-full px-2.5 py-0.5 font-semibold">
                                                                No subscription
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                {plan !== 'enterprise' && (
                                                    <a href="/pricing" className="shrink-0">
                                                        <Button size="sm" className="bg-white text-slate-900 hover:bg-white/90 font-bold shadow-lg">
                                                            <Zap className="h-3.5 w-3.5 mr-1.5 text-amber-500" /> Upgrade
                                                        </Button>
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                        <CardContent className="p-5">
                                            <div className="grid sm:grid-cols-2 gap-6">
                                                <div>
                                                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">What&apos;s Included</p>
                                                    <ul className="space-y-2">
                                                        {(PLAN_FEATURES[plan] || PLAN_FEATURES.starter).map((f) => (
                                                            <li key={f} className="flex items-start gap-2 text-sm">
                                                                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />
                                                                <span>{f}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">Usage</p>
                                                    <div className="space-y-4">
                                                        <div>
                                                            <div className="flex items-center justify-between mb-2">
                                                                <div className="flex items-center gap-1.5 text-sm font-medium">
                                                                    <Users className="h-3.5 w-3.5 text-muted-foreground" /> Staff Members
                                                                </div>
                                                                <span className={`text-xs font-mono font-bold ${nearLimit ? 'text-rose-500' : 'text-muted-foreground'}`}>
                                                                    {storeUsers.length} / {userLimit === 9999 ? '∞' : userLimit}
                                                                </span>
                                                            </div>
                                                            <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                                                                <div
                                                                    className={`h-full rounded-full transition-all duration-500 ${nearLimit ? 'bg-rose-500' : 'bg-cyan-500'}`}
                                                                    style={{ width: `${usedPct}%` }}
                                                                />
                                                            </div>
                                                            {nearLimit && (
                                                                <p className="text-xs text-rose-500 mt-1.5 flex items-center gap-1">
                                                                    <AlertCircle className="h-3 w-3" /> Near user limit — upgrade for more staff
                                                                </p>
                                                            )}
                                                        </div>
                                                        {plan !== 'enterprise' && (
                                                            <div className="pt-2 border-t">
                                                                <p className="text-xs text-muted-foreground mb-2">Want more features?</p>
                                                                <a href="/pricing">
                                                                    <Button variant="outline" size="sm" className="text-xs h-8 gap-1.5">
                                                                        <ChevronRight className="h-3.5 w-3.5" /> View all plans
                                                                    </Button>
                                                                </a>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })()}

                            {user?.store_id && (
                                <Card className="shadow-sm">
                                    <CardHeader className="pb-3">
                                        <CardTitle className="flex items-center gap-2 text-base">
                                            <Building className="h-4 w-4 text-cyan-500" /> Business Information
                                        </CardTitle>
                                        <CardDescription>Your business name, address, and contact details</CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="grid sm:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>Business Name</Label>
                                                <Input value={storeInfo.name} onChange={e => setStoreInfo({ ...storeInfo, name: e.target.value })} placeholder="H&S Smoothies & Breakfast" />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Email</Label>
                                                <Input type="email" value={storeInfo.email} onChange={e => setStoreInfo({ ...storeInfo, email: e.target.value })} placeholder="info@yourbusiness.com" />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Phone</Label>
                                                <Input value={storeInfo.phone} onChange={e => setStoreInfo({ ...storeInfo, phone: e.target.value })} placeholder="(555) 123-4567" />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Address</Label>
                                                <Input value={storeInfo.address} onChange={e => setStoreInfo({ ...storeInfo, address: e.target.value })} placeholder="123 Main St, City, State" />
                                            </div>
                                        </div>
                                        <div className="flex justify-end pt-2">
                                            <Button onClick={handleSaveInfo} disabled={savingInfo} className="bg-cyan-500 hover:bg-cyan-600 text-white">
                                                {savingInfo && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                                Save Business Info
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {user?.store_id && (
                                <Card className="shadow-sm">
                                    <CardHeader className="pb-3">
                                        <CardTitle className="flex items-center gap-2 text-base">
                                            <SettingsIcon className="h-4 w-4 text-cyan-500" /> Business Type
                                        </CardTitle>
                                        <CardDescription>Select your type to auto-configure categories and tools for your business</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                            {BUSINESS_TYPE_OPTIONS.map((bt) => {
                                                const isActive = businessType === bt.key;
                                                return (
                                                    <button
                                                        key={bt.key}
                                                        onClick={() => !applyingTemplate && handleApplyTemplate(bt.key)}
                                                        disabled={applyingTemplate}
                                                        className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border text-center transition-all cursor-pointer ${isActive ? bt.bg + ' ring-2 ring-cyan-500 shadow-sm' : 'border-border hover:border-cyan-500/40 bg-muted/40 hover:bg-muted/80'}`}
                                                    >
                                                        {isActive && <CheckCircle2 className="absolute top-2 right-2 h-3.5 w-3.5 text-cyan-500" />}
                                                        {applyingTemplate && !isActive ? <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /> : <bt.icon className={`h-6 w-6 ${isActive ? bt.color : 'text-muted-foreground'}`} />}
                                                        <p className={`text-xs font-semibold leading-tight ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>{bt.label}</p>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-3">Applying a new type adds its default categories (existing categories are kept).</p>
                                    </CardContent>
                                </Card>
                            )}

                            {!user?.store_id && (
                                <Card className="shadow-sm">
                                    <CardContent className="py-10 text-center">
                                        <Building className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                                        <p className="font-semibold text-muted-foreground">Business settings are available to store accounts</p>
                                    </CardContent>
                                </Card>
                            )}
                        </TabsContent>
                    )}

                    {/* ── PAYMENTS TAB ── */}
                    {isAdmin && (
                        <TabsContent value="payments" className="space-y-6">
                            {user?.store_id ? (
                                <>
                                    {/* Stripe Connect Settings */}
                                    <StripeConnectSettings />
                                    
                                    {/* Payment & Tax Settings */}
                                    <Card className="shadow-sm">
                                        <CardHeader className="pb-3">
                                            <CardTitle className="flex items-center gap-2 text-base">
                                                <CreditCard className="h-4 w-4 text-cyan-500" /> Payment & Tax Settings
                                            </CardTitle>
                                            <CardDescription>Configure card fees and tax rates applied at checkout</CardDescription>
                                        </CardHeader>
                                    <CardContent className="space-y-6">
                                        <div>
                                            <p className="text-sm font-semibold mb-3">Card Processing Fees</p>
                                            <div className="grid sm:grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label className="flex items-center gap-1.5 text-xs text-muted-foreground font-semibold uppercase tracking-wide">
                                                        <Percent className="h-3 w-3" /> Percentage Fee
                                                    </Label>
                                                    <div className="relative">
                                                        <Input type="number" step="0.1" min="0" max="10" value={storeSettings.card_fee_percentage}
                                                            onChange={e => setStoreSettings({ ...storeSettings, card_fee_percentage: e.target.value })} placeholder="2.5" className="pr-8" />
                                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono text-sm">%</span>
                                                    </div>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="flex items-center gap-1.5 text-xs text-muted-foreground font-semibold uppercase tracking-wide">
                                                        <DollarSign className="h-3 w-3" /> Flat Fee
                                                    </Label>
                                                    <div className="relative">
                                                        <Input type="number" step="0.01" min="0" value={storeSettings.card_fee_flat}
                                                            onChange={e => setStoreSettings({ ...storeSettings, card_fee_flat: e.target.value })} placeholder="0.30" className="pl-7" />
                                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono text-sm">$</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <Separator />
                                        <div className="space-y-2">
                                            <Label className="flex items-center gap-1.5 text-xs text-muted-foreground font-semibold uppercase tracking-wide">
                                                <Percent className="h-3 w-3" /> Default Tax Rate
                                            </Label>
                                            <div className="relative max-w-xs">
                                                <Input type="number" step="0.1" min="0" max="30" value={storeSettings.tax_rate}
                                                    onChange={e => setStoreSettings({ ...storeSettings, tax_rate: e.target.value })} placeholder="8" className="pr-8" />
                                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono text-sm">%</span>
                                            </div>
                                        </div>
                                        <Separator />
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-semibold text-sm">Show fee to customer</p>
                                                <p className="text-xs text-muted-foreground mt-0.5">Display card fee as a separate line item on receipts</p>
                                            </div>
                                            <Switch checked={storeSettings.show_fee_to_customer}
                                                onCheckedChange={checked => setStoreSettings({ ...storeSettings, show_fee_to_customer: checked })} />
                                        </div>
                                        <div className="flex justify-end pt-2">
                                            <Button onClick={handleSaveFees} disabled={savingFees} className="bg-cyan-500 hover:bg-cyan-600 text-white">
                                                {savingFees && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                                Save Payment Settings
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                                </>
                            ) : (
                                <Card className="shadow-sm">
                                    <CardContent className="py-10 text-center">
                                        <CreditCard className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                                        <p className="font-semibold text-muted-foreground">Payment settings are available to store admins only</p>
                                    </CardContent>
                                </Card>
                            )}
                        </TabsContent>
                    )}

                    {/* ── USERS TAB ── */}
                    {canManageUsers && (
                        <TabsContent value="users" className="space-y-6">
                            <Card className="shadow-sm overflow-hidden">
                                <CardHeader className="pb-3">
                                    <div className="flex items-center justify-between flex-wrap gap-3">
                                        <div>
                                            <CardTitle className="flex items-center gap-2 text-base">
                                                <Users className="h-4 w-4 text-cyan-500" /> Team Management
                                            </CardTitle>
                                            <CardDescription className="mt-1">
                                                {!isSuperAdmin && user?.store_id && (
                                                    <span className="flex items-center gap-2 flex-wrap">
                                                        <span>{storeUsers.length} / {userLimit === 9999 ? '∞' : userLimit} users used</span>
                                                        <Badge variant="outline" className="capitalize text-xs">{plan}</Badge>
                                                    </span>
                                                )}
                                                {isSuperAdmin && 'All registered users across the platform'}
                                                {isManager && 'Manage your cashiers and staff'}
                                            </CardDescription>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {user?.store_id && storeUsers.length >= userLimit && isAdmin && (
                                                <Badge className="bg-rose-500/10 text-rose-600 border-rose-500/20 text-xs">User limit reached</Badge>
                                            )}
                                            {user?.store_id && (isManager || storeUsers.length < userLimit) && (
                                                <Button size="sm" onClick={() => setShowAddUser(!showAddUser)}
                                                    className="bg-cyan-500 hover:bg-cyan-600 text-white">
                                                    <UserPlus className="h-4 w-4 mr-1.5" /> Add Staff
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </CardHeader>

                                {showAddUser && (
                                    <CardContent className="border-b pb-6">
                                        <div className="bg-muted/40 rounded-xl p-5 border border-border">
                                            <div className="flex items-center gap-2 mb-4">
                                                <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                                                    <UserPlus className="h-4 w-4 text-cyan-500" />
                                                </div>
                                                <p className="font-bold text-foreground">New Staff Member</p>
                                            </div>
                                            <form onSubmit={handleAddUser} className="space-y-4">
                                                <div className="grid sm:grid-cols-2 gap-4">
                                                    <div className="space-y-2">
                                                        <Label>Full Name <span className="text-red-500">*</span></Label>
                                                        <Input required value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} placeholder="Jane Smith" />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Email Address <span className="text-red-500">*</span></Label>
                                                        <Input required type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} placeholder="jane@yourbusiness.com" />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Password <span className="text-red-500">*</span></Label>
                                                        <div className="relative">
                                                            <Input
                                                                required
                                                                type={showNewPassword ? 'text' : 'password'}
                                                                value={newUser.password}
                                                                onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                                                                placeholder="Min 8 chars, uppercase, number"
                                                                className="pr-10"
                                                            />
                                                            <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                                                                {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Job Title / Position</Label>
                                                        <div className="flex gap-2">
                                                            <Input
                                                                list="job-title-options"
                                                                value={newUser.job_title}
                                                                onChange={e => setNewUser({ ...newUser, job_title: e.target.value })}
                                                                placeholder="e.g. Cashier, Server, Bartender"
                                                            />
                                                            <datalist id="job-title-options">
                                                                {JOB_TITLE_OPTIONS.map(t => <option key={t} value={t} />)}
                                                            </datalist>
                                                        </div>
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Role</Label>
                                                        <Select value={newUser.role} onValueChange={v => setNewUser({ ...newUser, role: v })}>
                                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="cashier">Cashier — Can process sales</SelectItem>
                                                                {isAdmin && <SelectItem value="manager">Manager — Can manage products & reports</SelectItem>}
                                                                {isAdmin && <SelectItem value="admin">Admin — Full access</SelectItem>}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>

                                                <Separator />

                                                <div>
                                                    <div className="flex items-center gap-2 mb-3">
                                                        <KeyRound className="h-4 w-4 text-cyan-500" />
                                                        <p className="font-semibold text-sm">POS & Time Clock PIN</p>
                                                        <Badge variant="outline" className="text-xs text-muted-foreground">Optional</Badge>
                                                    </div>
                                                    <p className="text-xs text-muted-foreground mb-3">A 4-digit PIN lets this employee clock in/out and access the POS without a password. You can set it now or later.</p>
                                                    <div className="grid sm:grid-cols-2 gap-4">
                                                        <div className="space-y-2">
                                                            <Label>4-Digit PIN</Label>
                                                            <Input
                                                                type="password"
                                                                inputMode="numeric"
                                                                maxLength={4}
                                                                value={newUser.pin}
                                                                onChange={e => setNewUser({ ...newUser, pin: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                                                                placeholder="••••"
                                                                className="text-center text-xl tracking-[0.5em] font-mono"
                                                            />
                                                        </div>
                                                        <div className="space-y-2">
                                                            <Label>Confirm PIN</Label>
                                                            <Input
                                                                type="password"
                                                                inputMode="numeric"
                                                                maxLength={4}
                                                                value={newUser.confirmPin}
                                                                onChange={e => setNewUser({ ...newUser, confirmPin: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                                                                placeholder="••••"
                                                                className="text-center text-xl tracking-[0.5em] font-mono"
                                                            />
                                                        </div>
                                                    </div>
                                                    {newUser.pin && newUser.confirmPin && newUser.pin !== newUser.confirmPin && (
                                                        <p className="flex items-center gap-1.5 text-xs text-red-500 mt-2">
                                                            <AlertCircle className="h-3.5 w-3.5" /> PINs do not match
                                                        </p>
                                                    )}
                                                    {newUser.pin && newUser.confirmPin && newUser.pin === newUser.confirmPin && (
                                                        <p className="flex items-center gap-1.5 text-xs text-emerald-600 mt-2">
                                                            <CheckCircle2 className="h-3.5 w-3.5" /> PINs match
                                                        </p>
                                                    )}
                                                </div>

                                                <div className="flex gap-2 justify-end pt-2">
                                                    <Button type="button" variant="outline" onClick={() => setShowAddUser(false)}>Cancel</Button>
                                                    <Button type="submit" disabled={addingUser} className="bg-cyan-500 hover:bg-cyan-600 text-white">
                                                        {addingUser && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                                        Create Staff Member
                                                    </Button>
                                                </div>
                                            </form>
                                        </div>
                                    </CardContent>
                                )}

                                <CardContent className="p-0">
                                    {loading ? (
                                        <div className="p-6 space-y-3">
                                            {[...Array(3)].map((_, i) => <div key={i} className="h-12 bg-muted/50 rounded-lg animate-pulse" />)}
                                        </div>
                                    ) : (
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="bg-muted/50 hover:bg-muted/50">
                                                    <TableHead className="font-semibold">Name / Position</TableHead>
                                                    <TableHead className="font-semibold">Email</TableHead>
                                                    <TableHead className="font-semibold">Role</TableHead>
                                                    <TableHead className="font-semibold">PIN</TableHead>
                                                    <TableHead className="font-semibold">Joined</TableHead>
                                                    <TableHead className="w-[50px]" />
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {users.map((u) => {
                                                    const pinInfo = pinStatuses[u.id];
                                                    const hasPin = pinInfo?.has_pin === true;
                                                    const isLocked = pinInfo?.is_locked === true;
                                                    return (
                                                        <TableRow key={u.id} className="group">
                                                            <TableCell className="font-semibold">
                                                                <div className="flex items-center gap-2.5">
                                                                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center shrink-0 shadow-sm">
                                                                        <span className="text-xs font-black text-white">{u.name?.charAt(0)?.toUpperCase()}</span>
                                                                    </div>
                                                                    <div>
                                                                        <div className="flex items-center gap-1.5">
                                                                            <span>{u.name}</span>
                                                                            {u.id === user?.id && (
                                                                                <Badge className="bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20 text-xs">You</Badge>
                                                                            )}
                                                                        </div>
                                                                        {u.job_title && (
                                                                            <p className="text-xs text-muted-foreground">{u.job_title}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="text-muted-foreground text-sm">{u.email}</TableCell>
                                                            <TableCell>
                                                                {isAdmin ? (
                                                                    <Select value={u.role} onValueChange={v => handleRoleChange(u.id, v)} disabled={u.id === user?.id}>
                                                                        <SelectTrigger className="w-[130px] h-8 text-xs">
                                                                            <SelectValue />
                                                                        </SelectTrigger>
                                                                        <SelectContent>
                                                                            <SelectItem value="admin"><span className="flex items-center gap-2"><Shield className="h-3 w-3 text-rose-500" />Admin</span></SelectItem>
                                                                            <SelectItem value="manager">Manager</SelectItem>
                                                                            <SelectItem value="cashier">Cashier</SelectItem>
                                                                        </SelectContent>
                                                                    </Select>
                                                                ) : (
                                                                    <Badge className={`capitalize text-xs border ${ROLE_COLORS[u.role] || ROLE_COLORS.cashier}`} variant="outline">
                                                                        {u.role}
                                                                    </Badge>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                {isLocked ? (
                                                                    <Badge className="bg-red-500/10 text-red-600 border-red-500/20 text-xs gap-1" variant="outline">
                                                                        <AlertCircle className="h-3 w-3" /> Locked
                                                                    </Badge>
                                                                ) : hasPin ? (
                                                                    <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-xs gap-1" variant="outline">
                                                                        <BadgeCheck className="h-3 w-3" /> PIN Set
                                                                    </Badge>
                                                                ) : (
                                                                    <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20 text-xs gap-1" variant="outline">
                                                                        <KeyRound className="h-3 w-3" /> No PIN
                                                                    </Badge>
                                                                )}
                                                            </TableCell>
                                                            <TableCell className="text-muted-foreground text-sm">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                                                            <TableCell>
                                                                {u.id !== user?.id && (
                                                                    <DropdownMenu>
                                                                        <DropdownMenuTrigger asChild>
                                                                            <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                                                                                <MoreVertical className="h-4 w-4" />
                                                                            </Button>
                                                                        </DropdownMenuTrigger>
                                                                        <DropdownMenuContent align="end">
                                                                            <DropdownMenuItem onClick={() => handleOpenSetPin(u)} className="gap-2">
                                                                                <KeyRound className="h-4 w-4 text-cyan-500" />
                                                                                {hasPin ? 'Reset PIN' : 'Set PIN'}
                                                                            </DropdownMenuItem>
                                                                            <DropdownMenuSeparator />
                                                                            <DropdownMenuItem onClick={() => handleDeleteUser(u.id)} className="text-destructive gap-2">
                                                                                <Trash2 className="h-4 w-4" /> Delete User
                                                                            </DropdownMenuItem>
                                                                        </DropdownMenuContent>
                                                                    </DropdownMenu>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                                {users.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={6} className="py-10 text-center">
                                                            <Users className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                                                            <p className="text-muted-foreground font-medium text-sm">No staff members yet</p>
                                                            <p className="text-xs text-muted-foreground mt-1">Click "Add Staff" to create your first team member</p>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    )}
                                </CardContent>

                                {user?.store_id && (
                                    <div className="p-4 border-t bg-muted/20">
                                        <div className="flex items-start gap-2 text-xs text-muted-foreground">
                                            <KeyRound className="h-3.5 w-3.5 mt-0.5 shrink-0 text-cyan-500" />
                                            <p>Staff use their <strong className="text-foreground">4-digit PIN</strong> to clock in/out and access the POS terminal. Passwords are for web login only.</p>
                                        </div>
                                    </div>
                                )}
                            </Card>
                        </TabsContent>
                    )}

                    {/* ── WEBHOOKS TAB ── */}
                    <TabsContent value="webhooks" className="space-y-6">
                        <WebhookManager />
                    </TabsContent>

                    {/* ── INTEGRATIONS / API KEYS TAB ── */}
                    <TabsContent value="integrations" className="space-y-6">
                        <ApiKeyManager />
                    </TabsContent>

                    {/* ── SYSTEM TAB ── */}
                    <TabsContent value="system" className="space-y-6">
                        <Card className="shadow-sm">
                            <CardHeader className="pb-3">
                                <CardTitle className="flex items-center gap-2 text-base">
                                    <Palette className="h-4 w-4 text-cyan-500" /> Appearance
                                </CardTitle>
                                <CardDescription>Customize the look and feel of your POS</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-semibold text-sm">Color Theme</p>
                                        <p className="text-xs text-muted-foreground mt-0.5">Choose between light, dark, or match your system</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button variant={theme === 'light' ? 'default' : 'outline'} size="sm"
                                            onClick={() => setTheme('light')}
                                            className={theme === 'light' ? 'bg-cyan-500 hover:bg-cyan-600 text-white' : ''}>
                                            <Sun className="h-3.5 w-3.5 mr-1.5" />Light
                                        </Button>
                                        <Button variant={theme === 'dark' ? 'default' : 'outline'} size="sm"
                                            onClick={() => setTheme('dark')}
                                            className={theme === 'dark' ? 'bg-cyan-500 hover:bg-cyan-600 text-white' : ''}>
                                            <Moon className="h-3.5 w-3.5 mr-1.5" />Dark
                                        </Button>
                                        <Button variant={theme === 'system' ? 'default' : 'outline'} size="sm"
                                            onClick={() => setTheme('system')}
                                            className={theme === 'system' ? 'bg-cyan-500 hover:bg-cyan-600 text-white' : ''}>
                                            <Monitor className="h-3.5 w-3.5 mr-1.5" />System
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {isAdmin && (
                            <Card className="shadow-sm">
                                <CardHeader className="pb-3">
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <Clock className="h-4 w-4 text-cyan-500" /> Time Clock
                                    </CardTitle>
                                    <CardDescription>Configure employee clock-in / clock-out requirements</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-5">
                                    <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
                                        <div>
                                            <p className="font-semibold text-sm">Require Clock-In to Use POS</p>
                                            <p className="text-xs text-muted-foreground mt-0.5">Employees must clock in with their PIN before accessing the POS terminal</p>
                                        </div>
                                        <Switch
                                            checked={timeclockSettings.require_clockin_for_pos}
                                            onCheckedChange={checked => setTimeclockSettings(prev => ({ ...prev, require_clockin_for_pos: checked }))}
                                        />
                                    </div>
                                    <Button onClick={handleSaveTimeclock} disabled={savingTimeclock} className="bg-cyan-500 hover:bg-cyan-600 text-white">
                                        {savingTimeclock && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                        Save Time Clock Settings
                                    </Button>
                                </CardContent>
                            </Card>
                        )}

                        <Card className="shadow-sm">
                            <CardHeader className="pb-3">
                                <CardTitle className="flex items-center gap-2 text-base">
                                    <User className="h-4 w-4 text-cyan-500" /> Your Account
                                </CardTitle>
                                <CardDescription>Your profile information</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center gap-4 p-4 rounded-xl bg-muted/50">
                                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center shrink-0">
                                        <span className="text-xl font-black text-white">{user?.name?.charAt(0)?.toUpperCase()}</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-lg truncate">{user?.name}</p>
                                        <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
                                        <div className="flex items-center gap-2 mt-1.5">
                                            <Badge className={`capitalize text-xs border ${ROLE_COLORS[user?.role] || ROLE_COLORS.cashier}`} variant="outline">
                                                {user?.role}
                                            </Badge>
                                            {user?.created_at && (
                                                <span className="text-xs text-muted-foreground">Since {new Date(user.created_at).toLocaleDateString()}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Security / Change Password */}
                        <Card className="shadow-sm">
                            <CardHeader className="pb-3">
                                <CardTitle className="flex items-center gap-2 text-base">
                                    <Lock className="h-4 w-4 text-cyan-500" /> Security
                                </CardTitle>
                                <CardDescription>Manage your password and account security</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {!showChangePassword ? (
                                    <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
                                        <div>
                                            <p className="font-semibold text-sm">Password</p>
                                            <p className="text-xs text-muted-foreground mt-0.5">Update your login password</p>
                                        </div>
                                        <Button variant="outline" size="sm" onClick={() => setShowChangePassword(true)}>
                                            Change Password
                                        </Button>
                                    </div>
                                ) : (
                                    <form onSubmit={handleChangePassword} className="space-y-4 max-w-sm">
                                        <div className="space-y-2">
                                            <Label>Current Password</Label>
                                            <div className="relative">
                                                <Input
                                                    type={showCurrentPw ? 'text' : 'password'}
                                                    value={pwForm.current}
                                                    onChange={e => setPwForm({ ...pwForm, current: e.target.value })}
                                                    placeholder="Enter current password"
                                                    className="pr-10"
                                                />
                                                <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                                                    {showCurrentPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>New Password</Label>
                                            <Input
                                                type={showCurrentPw ? 'text' : 'password'}
                                                value={pwForm.newPw}
                                                onChange={e => setPwForm({ ...pwForm, newPw: e.target.value })}
                                                placeholder="Min 8 chars, uppercase, number"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Confirm New Password</Label>
                                            <Input
                                                type={showCurrentPw ? 'text' : 'password'}
                                                value={pwForm.confirmPw}
                                                onChange={e => setPwForm({ ...pwForm, confirmPw: e.target.value })}
                                                placeholder="Confirm new password"
                                            />
                                        </div>
                                        {pwForm.newPw && pwForm.confirmPw && pwForm.newPw !== pwForm.confirmPw && (
                                            <p className="text-xs text-red-500 flex items-center gap-1.5"><AlertCircle className="h-3.5 w-3.5" />Passwords do not match</p>
                                        )}
                                        <div className="flex gap-2 pt-2">
                                            <Button type="button" variant="outline" onClick={() => { setShowChangePassword(false); setPwForm({ current: '', newPw: '', confirmPw: '' }); }}>Cancel</Button>
                                            <Button type="submit" disabled={changingPw || !pwForm.current || !pwForm.newPw || pwForm.newPw !== pwForm.confirmPw} className="bg-cyan-500 hover:bg-cyan-600 text-white">
                                                {changingPw && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                                Update Password
                                            </Button>
                                        </div>
                                    </form>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

            {/* ── SET / RESET PIN DIALOG ── */}
            <Dialog open={setPinDialogOpen} onOpenChange={open => { setSetPinDialogOpen(open); if (!open) { setNewPinValue(''); setConfirmPinValue(''); } }}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <KeyRound className="h-5 w-5 text-cyan-500" />
                            {setPinTarget && (pinStatuses[setPinTarget.id]?.has_pin ? 'Reset PIN' : 'Set PIN')}
                        </DialogTitle>
                        <DialogDescription>
                            {setPinTarget && (
                                <>Set a 4-digit PIN for <strong>{setPinTarget.name}</strong>. They'll use it to clock in/out and access the POS terminal.</>
                            )}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        <div className="space-y-2">
                            <Label>New 4-Digit PIN</Label>
                            <Input
                                type="password"
                                inputMode="numeric"
                                maxLength={4}
                                value={newPinValue}
                                onChange={e => setNewPinValue(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                placeholder="••••"
                                className="text-center text-2xl tracking-[0.6em] font-mono h-14"
                                autoFocus
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Confirm PIN</Label>
                            <Input
                                type="password"
                                inputMode="numeric"
                                maxLength={4}
                                value={confirmPinValue}
                                onChange={e => setConfirmPinValue(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                placeholder="••••"
                                className="text-center text-2xl tracking-[0.6em] font-mono h-14"
                            />
                        </div>
                        {newPinValue && confirmPinValue && newPinValue !== confirmPinValue && (
                            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                                <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
                                <p className="text-xs text-red-600">PINs do not match</p>
                            </div>
                        )}
                        {newPinValue.length === 4 && confirmPinValue.length === 4 && newPinValue === confirmPinValue && (
                            <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                                <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                                <p className="text-xs text-emerald-600">PINs match — ready to save</p>
                            </div>
                        )}
                    </div>

                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setSetPinDialogOpen(false)}>Cancel</Button>
                        <Button
                            onClick={handleSavePin}
                            disabled={savingPin || newPinValue.length !== 4 || newPinValue !== confirmPinValue}
                            className="bg-cyan-500 hover:bg-cyan-600 text-white"
                        >
                            {savingPin && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                            Save PIN
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
